import React, { useEffect, useRef, useState } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const QRScanner = () => {
  const scannerRef = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [scanSuccess, setScanSuccess] = useState(false);
  const [isScanning, setIsScanning] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [scannedBooking, setScannedBooking] = useState(null);
  const [earlyCheckinData, setEarlyCheckinData] = useState(null);
  const [hotelInfo, setHotelInfo] = useState({ name: '', email: '' });

  useEffect(() => {
    // Get hotel info from localStorage
    try {
      const hotelPropertyCache = localStorage.getItem('hotelPropertyCache');
      if (hotelPropertyCache) {
        const hotelData = JSON.parse(hotelPropertyCache);
        if (hotelData.data) {
          setHotelInfo({
            name: hotelData.data.propertyname || hotelData.data.name || 'The Pagoda Hotel',
            email: hotelData.data.email || 'tushar.bhosle@hotelpagoda.com',
          });
        }
      }
    } catch (error) {
      console.error('Error reading hotel data from localStorage:', error);
      // Set default values if unable to read from localStorage
      setHotelInfo({
        name: 'The Pagoda Hotel',
        email: 'tushar.bhosle@hotelpagoda.com',
      });
    }
  }, []);

  const shouldShowDisclaimer = (checkInDate) => {
    const now = new Date();
    const checkInDateTime = new Date(checkInDate);

    // Set check-in time to 11:00 AM
    checkInDateTime.setHours(11, 0, 0, 0);

    // Calculate 2 hours prior to check-in time (11:00 AM)
    const twoHoursPrior = new Date(checkInDateTime);
    twoHoursPrior.setHours(twoHoursPrior.getHours() - 2);

    // If current time is before the 2 hours prior to check-in, show disclaimer
    return now < twoHoursPrior;
  };

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  const decryptUserData = async (encryptedData) => {
    try {
      if (encryptedData.email && encryptedData.email.length < 50) {
        return encryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: {
            email: encryptedData.email,
            phone_number: encryptedData.phone_number,
            country_code: encryptedData.country_code,
          },
        }),
      });

      if (response.status === 401) {
        return encryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  const getGuestData = async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) return null;

    try {
      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) return null;

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

      if (isDecrypted) {
        return {
          firstname: profileData.firstname || '',
          lastname: profileData.lastname || '',
          email: profileData.emailid || '',
          phone: profileData.phonenumber || '',
        };
      }

      const decryptedFields = await decryptUserData({
        email: profileData.emailid,
        phone_number: profileData.phonenumber,
        country_code: profileData.countrycode,
      });

      return {
        firstname: profileData.firstname || '',
        lastname: profileData.lastname || '',
        email: decryptedFields.email || profileData.emailid || '',
        phone: decryptedFields.phone_number || profileData.phonenumber || '',
      };
    } catch (error) {
      console.error('Error fetching guest data:', error);
      return null;
    }
  };

  const parseQrData = async (qrData) => {
    try {
      if (qrData.startsWith('blob:')) {
        const response = await fetch(qrData);
        const blob = await response.blob();
        qrData = await blob.text();
      }
      let cleanData = qrData;
      if (cleanData.startsWith('"') && cleanData.endsWith('"')) {
        cleanData = cleanData.slice(1, -1);
      }
      cleanData = cleanData.replace(/\\"/g, '"');
      const parsed = JSON.parse(cleanData);

      // Handle both formats:
      // 1. With bookings array (original expected format)
      // 2. Direct booking object (your actual QR code format)
      if (parsed?.bookings?.length > 0) {
        const primaryBooking = parsed.bookings[0];
        return {
          ...parsed,
          ...primaryBooking,
          primaryBooking,
        };
      } else {
        // Direct booking object format
        return {
          ...parsed,
          guest_email: parsed.guest_email || '', // Will be filled by pre-checkin API
          guest_phone: parsed.guest_phone || '', // Will be filled by pre-checkin API
          bookings: [parsed],
          primaryBooking: parsed,
        };
      }
    } catch (error) {
      console.error('QR parsing error:', error);
      return null;
    }
  };

  const callPreCheckinAPI = async (orderId, lastName, email) => {
    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          order_id: orderId,
          last_name: lastName,
          email: email,
        }),
      });

      if (!response.ok) {
        throw new Error(`Pre-checkin failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Pre-checkin API error:', error);
      throw error;
    }
  };

  const validateBooking = async (bookingDetails, guestEmail) => {
    if (!bookingDetails) {
      return {
        isValid: false,
        message: 'Invalid QR code format. Please scan a valid reservation QR code.',
      };
    }

    if (bookingDetails.booking_status === 'Hard') {
      return { isValid: true, isHardBooking: true };
    }

    try {
      const checkinDateStr = bookingDetails.checkin_date;
      if (!checkinDateStr) {
        return {
          isValid: false,
          message: 'Reservation information incomplete. Missing check-in date.',
        };
      }

      const bookingDate = new Date(checkinDateStr);
      if (isNaN(bookingDate.getTime())) {
        return {
          isValid: false,
          message: 'Invalid reservation date format. Please contact support.',
        };
      }

      const today = new Date();
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const bookingOnly = new Date(
        bookingDate.getFullYear(),
        bookingDate.getMonth(),
        bookingDate.getDate()
      );

      if (bookingOnly.getTime() !== todayOnly.getTime()) {
        return {
          isValid: false,
          message: `Check-in is only available on your reserved date: ${bookingDate.toLocaleDateString(
            'en-GB',
            {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }
          )}. For early check-in, please contact our front desk.`,
        };
      }

      if (!guestEmail) {
        return {
          isValid: false,
          message: "Unable to verify your account. Please ensure you're logged in correctly.",
        };
      }

      // Email validation will be done by pre-checkin API
      return { isValid: true, isHardBooking: false };
    } catch (error) {
      console.error('Detailed validation error:', error);
      return {
        isValid: false,
        message:
          'We encountered an issue verifying your reservation. Please try again or contact our support team if the problem persists.',
      };
    }
  };
  const proceedWithCheckIn = async () => {
    setProcessing(true);
    try {
      const bookingDetails = scannedBooking;

      // Get guest data for email
      const guestData = await getGuestData();
      if (!guestData || !guestData.email) {
        throw new Error('Unable to get your account information. Please try logging in again.');
      }

      // Call pre-checkin API with guest's email
      const preCheckinResponse = await callPreCheckinAPI(
        bookingDetails.order_id,
        bookingDetails.guest_name.split(' ').pop(), // Last name
        guestData.email // Use guest's email
      );

      if (preCheckinResponse.success) {
        // Check if already checked in
        if (
          preCheckinResponse.bookings &&
          preCheckinResponse.bookings[0] &&
          preCheckinResponse.bookings[0].status_message === 'Already checked in'
        ) {
          // Merge the original booking details with the pre-checkin response
          const mergedBookingDetails = {
            ...bookingDetails,
            ...preCheckinResponse,
            primaryBooking: {
              ...bookingDetails.primaryBooking,
              ...(preCheckinResponse.bookings ? preCheckinResponse.bookings[0] : {}),
            },
            bookings: preCheckinResponse.bookings || bookingDetails.bookings,
          };

          // Store the merged booking details
          localStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
          sessionStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
          setScanSuccess(true);

          setTimeout(() => {
            navigate('/reservation/options/booking/welcome', {
              state: { bookingDetails: mergedBookingDetails },
            });
          }, 500);
        } else {
          // Merge the original booking details with the pre-checkin response
          const mergedBookingDetails = {
            ...bookingDetails,
            ...preCheckinResponse,
            primaryBooking: {
              ...bookingDetails.primaryBooking,
              ...(preCheckinResponse.bookings ? preCheckinResponse.bookings[0] : {}),
            },
            bookings: preCheckinResponse.bookings || bookingDetails.bookings,
          };

          // Store the merged booking details
          localStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
          sessionStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
          setScanSuccess(true);

          setTimeout(() => {
            navigate('/checkin-details', {
              state: { bookingDetails: mergedBookingDetails },
            });
          }, 500);
        }
      } else {
        throw new Error(preCheckinResponse.message || 'Pre-checkin verification failed');
      }
    } catch (error) {
      console.error('Pre-checkin error:', error);
      setErrorMessage(
        error.message || 'Failed to verify reservation. Please try again or contact support.'
      );
      setShowModal(true);
      setIsScanning(true);
    } finally {
      setProcessing(false);
      setShowDisclaimer(false);
    }
  };
  const handleScanSuccess = async (decodedText) => {
    if (!isScanning) return;
    setIsScanning(false);

    const bookingDetails = await parseQrData(decodedText);

    // Get guest data to use their email for pre-checkin
    const guestData = await getGuestData();
    if (!guestData || !guestData.email) {
      setErrorMessage("Unable to verify your account. Please ensure you're logged in correctly.");
      setShowModal(true);
      setIsScanning(true);
      return;
    }

    // Use guest's email for validation
    const validation = await validateBooking(bookingDetails, guestData.email);

    if (!validation.isValid) {
      setErrorMessage(validation.message);
      setShowModal(true);
      setIsScanning(true);
      return;
    }

    if (bookingDetails) {
      try {
        // Check if early check-in disclaimer should be shown
        if (shouldShowDisclaimer(bookingDetails.checkin_date)) {
          // Call pre-checkin API with guest's email
          const preCheckinResponse = await callPreCheckinAPI(
            bookingDetails.order_id,
            bookingDetails.guest_name.split(' ').pop(), // Last name
            guestData.email // Use guest's email from profile
          );

          // Check if already checked in
          if (
            preCheckinResponse.bookings &&
            preCheckinResponse.bookings[0] &&
            preCheckinResponse.bookings[0].status_message === 'Already checked in'
          ) {
            // Merge the original booking details with the pre-checkin response
            const mergedBookingDetails = {
              ...bookingDetails,
              ...preCheckinResponse,
              primaryBooking: {
                ...bookingDetails.primaryBooking,
                ...(preCheckinResponse.bookings ? preCheckinResponse.bookings[0] : {}),
              },
              bookings: preCheckinResponse.bookings || bookingDetails.bookings,
            };

            // Store the merged booking details
            localStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
            sessionStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
            setScanSuccess(true);

            setTimeout(() => {
              navigate('/reservation/options/booking/welcome', {
                state: { bookingDetails: mergedBookingDetails },
              });
            }, 500);
            return;
          }

          // Store early check-in data for the disclaimer
          if (
            preCheckinResponse.success &&
            preCheckinResponse.bookings &&
            preCheckinResponse.bookings[0] &&
            preCheckinResponse.bookings[0].early_checkin
          ) {
            setEarlyCheckinData(preCheckinResponse.bookings[0].early_checkin);
          }

          setScannedBooking(bookingDetails);
          setShowDisclaimer(true);
          return;
        }

        // If no disclaimer needed, proceed directly with check-in
        const preCheckinResponse = await callPreCheckinAPI(
          bookingDetails.order_id,
          bookingDetails.guest_name.split(' ').pop(), // Last name
          guestData.email // Use guest's email from profile
        );

        if (preCheckinResponse.success) {
          // Check if already checked in
          if (
            preCheckinResponse.bookings &&
            preCheckinResponse.bookings[0] &&
            preCheckinResponse.bookings[0].status_message === 'Already checked in'
          ) {
            // Merge the original booking details with the pre-checkin response
            const mergedBookingDetails = {
              ...bookingDetails,
              ...preCheckinResponse,
              primaryBooking: {
                ...bookingDetails.primaryBooking,
                ...(preCheckinResponse.bookings ? preCheckinResponse.bookings[0] : {}),
              },
              bookings: preCheckinResponse.bookings || bookingDetails.bookings,
            };

            // Store the merged booking details
            localStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
            sessionStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
            setScanSuccess(true);

            setTimeout(() => {
              navigate('/reservation/options/booking/welcome', {
                state: { bookingDetails: mergedBookingDetails },
              });
            }, 500);
          } else {
            // Merge the original booking details with the pre-checkin response
            const mergedBookingDetails = {
              ...bookingDetails,
              ...preCheckinResponse,
              primaryBooking: {
                ...bookingDetails.primaryBooking,
                ...(preCheckinResponse.bookings ? preCheckinResponse.bookings[0] : {}),
              },
              bookings: preCheckinResponse.bookings || bookingDetails.bookings,
            };

            // Store the merged booking details
            localStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
            sessionStorage.setItem('bookingDetails', JSON.stringify(mergedBookingDetails));
            setScanSuccess(true);

            setTimeout(() => {
              navigate(
                validation.isHardBooking
                  ? '/reservation/options/booking/welcome'
                  : '/checkin-details',
                { state: { bookingDetails: mergedBookingDetails } }
              );
            }, 500);
          }
        } else {
          throw new Error(preCheckinResponse.message || 'Pre-checkin verification failed');
        }
      } catch (error) {
        console.error('Pre-checkin error:', error);
        setErrorMessage(
          error.message || 'Failed to verify reservation. Please try again or contact support.'
        );
        setShowModal(true);
        setIsScanning(true);
      }
    } else {
      setErrorMessage('Invalid QR code data.');
      setShowModal(true);
      setIsScanning(true);
    }
  };

  useEffect(() => {
    const scanner = new Html5QrcodeScanner('qr-reader', {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      rememberLastUsedCamera: true,
    });

    scanner.render(handleScanSuccess, (error) => {
      if (!error.includes('NotFoundException')) {
        console.warn('Scan error:', error);
      }
    });

    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      .html5-qrcode-branding {
        display: none !important;
      }
      
      .html5-qrcode-report-issue-link {
        display: none !important;
      }

      .html5-qrcode-element {
        margin: 12px auto;
        padding: 10px 24px;
        background: #000;
        color: white;
        border: none;
        border-radius: 8px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
        display: block;
        width: 80%;
        font-size: 14px;
      }
      
      #html5-qrcode-anchor-scan-type-change {
        margin: 12px auto;
        padding: 10px 24px;
        background: #f8f8f8;
        color: #333;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-weight: 500;
        cursor: pointer;
        display: block;
        width: 80%;
        font-size: 14px;
      }
      
      .html5-qrcode-button-container {
        position: relative;
        margin: 16px 0;
      }
      
      .html5-qrcode-button-container::before {
        content: "OR";
        position: absolute;
        top: -10px;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        padding: 0 12px;
        color: #666;
        font-size: 13px;
        font-weight: 500;
        z-index: 1;
      }
      
      .html5-qrcode-button-container::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: #ddd;
        z-index: 0;
      }
    `;
    document.head.appendChild(styleElement);

    scannerRef.current = scanner;

    return () => {
      scanner.clear().catch((e) => {
        console.warn('Error clearing scanner:', e);
      });
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-1 bg-gray-50 p-8">
      <div className="w-full max-w-md bg-white rounded-xl shadow-md overflow-hidden p-6">
        <h2 className="text-3xl font-bold text-center mb-2">Scan Your QR Code</h2>
        <h2 className="text-sm font-semibold text-gray-600 text-center mb-4">
          Please scan the QR code received in the confirmation email
        </h2>
        <div
          id="qr-reader"
          className="w-full h-full rounded-lg border-4 border-dashed border-blue-200 bg-gray-100 overflow-hidden relative"
        ></div>

        {scanSuccess && (
          <div className="absolute inset-0 flex items-center justify-center  rounded-lg">
            <div className="bg-white p-10 rounded-lg shadow-xl">
              <div className="flex justify-center mb-2">
                <svg
                  className="w-12 h-12 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-center font-semibold text-gray-800">Scanned Successfully!</p>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="relative bg-white rounded-lg shadow-lg max-w-[500px] w-full p-6 mx-4">
            <div className="text-center">
              <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-3" />
              <h2 className="text-gray-900 text-lg font-semibold mb-2">Unable to Proceed</h2>
              <p className="text-red-600 text-md font-medium mb-3 whitespace-pre-line">
                {errorMessage}
              </p>
              <p className="text-gray-700 text-sm mb-4 mt-2">
                The QR code has been sent to your registered email address upon reservation. For
                expired or incorrect reservations, please contact{' '}
                <a href={`mailto:${hotelInfo.email}`} className="text-blue-600 hover:underline">
                  {hotelInfo.name}
                </a>{' '}
                support at{' '}
                <a href={`mailto:${hotelInfo.email}`} className="text-blue-600 hover:underline">
                  {hotelInfo.email}
                </a>
                .
              </p>

              <button
                onClick={() => {
                  setShowModal(false);
                  setIsScanning(true);
                }}
                className="px-5 py-2 bg-black text-white rounded-md font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">Early Check-In Notice</h2>
            <p className="mb-4">
              You are attempting to check in before the standard check-in time of{' '}
              <strong>11:00 AM</strong>. Early check-in will be charged{' '}
              <strong>₹{earlyCheckinData?.extra_charge}</strong> and is subject to room
              availability.
            </p>

            <p className="mb-6 font-semibold">Would you like to proceed with the early check-in?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDisclaimer(false);
                  setIsScanning(true);
                }}
                disabled={processing}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={proceedWithCheckIn}
                disabled={processing}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner;

// import React, { useEffect, useRef, useState } from "react";
// import { Html5QrcodeScanner } from "html5-qrcode";
// import { useNavigate } from "react-router-dom";
// import { BsFillExclamationTriangleFill } from "react-icons/bs";
// import Cookies from "js-cookie";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const QRScanner = () => {
//   const scannerRef = useRef(null);
//   const navigate = useNavigate();
//   const [showModal, setShowModal] = useState(false);
//   const [showDisclaimer, setShowDisclaimer] = useState(false);
//   const [errorMessage, setErrorMessage] = useState("");
//   const [scanSuccess, setScanSuccess] = useState(false);
//   const [isScanning, setIsScanning] = useState(true);
//   const [processing, setProcessing] = useState(false);
//   const [scannedBooking, setScannedBooking] = useState(null);
//   const [earlyCheckinData, setEarlyCheckinData] = useState(null);
//   const [hotelInfo, setHotelInfo] = useState({ name: "", email: "" });

//   useEffect(() => {
//     // Get hotel info from localStorage
//     try {
//       const hotelPropertyCache = localStorage.getItem("hotelPropertyCache");
//       if (hotelPropertyCache) {
//         const hotelData = JSON.parse(hotelPropertyCache);
//         if (hotelData.data) {
//           setHotelInfo({
//             name:
//               hotelData.data.propertyname ||
//               hotelData.data.name ||
//               "The Pagoda Hotel",
//             email: hotelData.data.email || "tushar.bhosle@hotelpagoda.com",
//           });
//         }
//       }
//     } catch (error) {
//       console.error("Error reading hotel data from localStorage:", error);
//       // Set default values if unable to read from localStorage
//       setHotelInfo({
//         name: "The Pagoda Hotel",
//         email: "tushar.bhosle@hotelpagoda.com",
//       });
//     }
//   }, []);

//   const shouldShowDisclaimer = (checkInDate) => {
//     const now = new Date();
//     const checkInDateTime = new Date(checkInDate);

//     // Set check-in time to 1:00 PM
//     checkInDateTime.setHours(13, 0, 0, 0);

//     // Calculate 2 hours prior to check-in time (11:00 AM)
//     const twoHoursPrior = new Date(checkInDateTime);
//     twoHoursPrior.setHours(twoHoursPrior.getHours() - 2);

//     // If current time is before the 2 hours prior to check-in, show disclaimer
//     return now < twoHoursPrior;
//   };

//   const getGuestIdFromToken = (token) => {
//     try {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       return payload.sub;
//     } catch (e) {
//       console.error("Error parsing token:", e);
//       return null;
//     }
//   };

//   const decryptUserData = async (encryptedData) => {
//     try {
//       if (encryptedData.email && encryptedData.email.length < 50) {
//         return encryptedData;
//       }

//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           encrypted_fields: {
//             email: encryptedData.email,
//             phone_number: encryptedData.phone_number,
//             country_code: encryptedData.country_code,
//           },
//         }),
//       });

//       if (response.status === 401) {
//         return encryptedData;
//       }

//       if (!response.ok) {
//         throw new Error(`Decryption failed: ${response.statusText}`);
//       }

//       return await response.json();
//     } catch (error) {
//       console.error("Decryption error:", error);
//       return encryptedData;
//     }
//   };

//   const getGuestData = async () => {
//     const accessToken = Cookies.get("access_token");
//     if (!accessToken) return null;

//     try {
//       const guestId =
//         Cookies.get("guest_id") || getGuestIdFromToken(accessToken);
//       if (!guestId) return null;

//       const profileResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (!profileResponse.ok) {
//         throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//       }

//       const profileData = await profileResponse.json();
//       const isDecrypted =
//         profileData.emailid && profileData.emailid.length < 50;

//       if (isDecrypted) {
//         return {
//           firstname: profileData.firstname || "",
//           lastname: profileData.lastname || "",
//           email: profileData.emailid || "",
//           phone: profileData.phonenumber || "",
//         };
//       }

//       const decryptedFields = await decryptUserData({
//         email: profileData.emailid,
//         phone_number: profileData.phonenumber,
//         country_code: profileData.countrycode,
//       });

//       return {
//         firstname: profileData.firstname || "",
//         lastname: profileData.lastname || "",
//         email: decryptedFields.email || profileData.emailid || "",
//         phone: decryptedFields.phone_number || profileData.phonenumber || "",
//       };
//     } catch (error) {
//       console.error("Error fetching guest data:", error);
//       return null;
//     }
//   };

//   const parseQrData = async (qrData) => {
//     try {
//       if (qrData.startsWith("blob:")) {
//         const response = await fetch(qrData);
//         const blob = await response.blob();
//         qrData = await blob.text();
//       }
//       let cleanData = qrData;
//       if (cleanData.startsWith('"') && cleanData.endsWith('"')) {
//         cleanData = cleanData.slice(1, -1);
//       }
//       cleanData = cleanData.replace(/\\"/g, '"');
//       const parsed = JSON.parse(cleanData);
//       if (parsed?.bookings?.length > 0) {
//         const primaryBooking = parsed.bookings[0];
//         return {
//           ...parsed,
//           ...primaryBooking,
//           primaryBooking,
//         };
//       }
//       return null;
//     } catch (error) {
//       console.error("QR parsing error:", error);
//       return null;
//     }
//   };

//   const callPreCheckinAPI = async (orderId, lastName, email) => {
//     try {
//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin/`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Accept: "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           order_id: orderId,
//           last_name: lastName,
//           email: email,
//         }),
//       });

//       if (!response.ok) {
//         throw new Error(`Pre-checkin failed: ${response.statusText}`);
//       }

//       const data = await response.json();
//       return data;
//     } catch (error) {
//       console.error("Pre-checkin API error:", error);
//       throw error;
//     }
//   };

//   const validateBooking = async (bookingDetails) => {
//     if (!bookingDetails) {
//       return {
//         isValid: false,
//         message:
//           "Invalid QR code format. Please scan a valid reservation QR code.",
//       };
//     }

//     if (bookingDetails.booking_status === "Hard") {
//       return { isValid: true, isHardBooking: true };
//     }

//     try {
//       const checkinDateStr = bookingDetails.checkin_date;
//       if (!checkinDateStr) {
//         return {
//           isValid: false,
//           message: "Reservation information incomplete. Missing check-in date.",
//         };
//       }

//       const bookingDate = new Date(checkinDateStr);
//       if (isNaN(bookingDate.getTime())) {
//         return {
//           isValid: false,
//           message: "Invalid reservation date format. Please contact support.",
//         };
//       }

//       const today = new Date();
//       const todayOnly = new Date(
//         today.getFullYear(),
//         today.getMonth(),
//         today.getDate()
//       );
//       const bookingOnly = new Date(
//         bookingDate.getFullYear(),
//         bookingDate.getMonth(),
//         bookingDate.getDate()
//       );

//       if (bookingOnly.getTime() !== todayOnly.getTime()) {
//         return {
//           isValid: false,
//           message: `Check-in is only available on your reserved date: ${bookingDate.toLocaleDateString(
//             "en-GB",
//             {
//               day: "2-digit",
//               month: "short",
//               year: "numeric",
//             }
//           )}. For early check-in, please contact our front desk.`,
//         };
//       }

//       const guestData = await getGuestData();
//       if (!guestData) {
//         return {
//           isValid: false,
//           message:
//             "Unable to verify your account. Please ensure you're logged in correctly.",
//         };
//       }

//       const bookingEmail = bookingDetails.guest_email;
//       if (!bookingEmail) {
//         return {
//           isValid: false,
//           message: "Reservation information incomplete. Missing guest email.",
//         };
//       }

//       if (
//         typeof guestData.email !== "string" ||
//         typeof bookingEmail !== "string" ||
//         guestData.email.toLowerCase() !== bookingEmail.toLowerCase()
//       ) {
//         return {
//           isValid: false,
//           message:
//             "This reservation belongs to a different account. Please scan the QR code from your booking confirmation email.",
//         };
//       }

//       const guestPhoneClean = (guestData.phone || "")
//         .toString()
//         .replace(/\D/g, "");
//       const bookingPhoneClean = (bookingDetails.guest_phone || "")
//         .toString()
//         .replace(/\D/g, "");

//       if (
//         guestPhoneClean &&
//         bookingPhoneClean &&
//         guestPhoneClean !== bookingPhoneClean
//       ) {
//         return {
//           isValid: false,
//           message:
//             "Contact information doesn't match our records. Please verify your reservation details.",
//         };
//       }

//       return { isValid: true, isHardBooking: false };
//     } catch (error) {
//       console.error("Detailed validation error:", error);
//       return {
//         isValid: false,
//         message:
//           "We encountered an issue verifying your reservation. Please try again or contact our support team if the problem persists.",
//       };
//     }
//   };

//   const proceedWithCheckIn = async () => {
//     setProcessing(true);
//     try {
//       const bookingDetails = scannedBooking;

//       // Call pre-checkin API
//       const preCheckinResponse = await callPreCheckinAPI(
//         bookingDetails.order_id,
//         bookingDetails.guest_name.split(" ").pop(), // Last name
//         bookingDetails.guest_email
//       );

//       if (preCheckinResponse.success) {
//         // Check if already checked in
//         if (
//           preCheckinResponse.bookings &&
//           preCheckinResponse.bookings[0] &&
//           preCheckinResponse.bookings[0].status_message === "Already checked in"
//         ) {
//           // Merge the original booking details with the pre-checkin response
//           const mergedBookingDetails = {
//             ...bookingDetails,
//             ...preCheckinResponse,
//             primaryBooking: {
//               ...bookingDetails.primaryBooking,
//               ...(preCheckinResponse.bookings
//                 ? preCheckinResponse.bookings[0]
//                 : {}),
//             },
//             bookings: preCheckinResponse.bookings || bookingDetails.bookings,
//           };

//           // Store the merged booking details
//           localStorage.setItem(
//             "bookingDetails",
//             JSON.stringify(mergedBookingDetails)
//           );
//           sessionStorage.setItem(
//             "bookingDetails",
//             JSON.stringify(mergedBookingDetails)
//           );
//           setScanSuccess(true);

//           setTimeout(() => {
//             navigate("/reservation/options/booking/welcome", {
//               state: { bookingDetails: mergedBookingDetails },
//             });
//           }, 500);
//         } else {
//           // Merge the original booking details with the pre-checkin response
//           const mergedBookingDetails = {
//             ...bookingDetails,
//             ...preCheckinResponse,
//             primaryBooking: {
//               ...bookingDetails.primaryBooking,
//               ...(preCheckinResponse.bookings
//                 ? preCheckinResponse.bookings[0]
//                 : {}),
//             },
//             bookings: preCheckinResponse.bookings || bookingDetails.bookings,
//           };

//           // Store the merged booking details
//           localStorage.setItem(
//             "bookingDetails",
//             JSON.stringify(mergedBookingDetails)
//           );
//           sessionStorage.setItem(
//             "bookingDetails",
//             JSON.stringify(mergedBookingDetails)
//           );
//           setScanSuccess(true);

//           setTimeout(() => {
//             navigate("/checkin-details", {
//               state: { bookingDetails: mergedBookingDetails },
//             });
//           }, 500);
//         }
//       } else {
//         throw new Error(
//           preCheckinResponse.message || "Pre-checkin verification failed"
//         );
//       }
//     } catch (error) {
//       console.error("Pre-checkin error:", error);
//       setErrorMessage(
//         error.message ||
//           "Failed to verify reservation. Please try again or contact support."
//       );
//       setShowModal(true);
//       setIsScanning(true);
//     } finally {
//       setProcessing(false);
//       setShowDisclaimer(false);
//     }
//   };

//   const handleScanSuccess = async (decodedText) => {
//     if (!isScanning) return;
//     setIsScanning(false);
//     console.log("Raw QR data:", decodedText); // Add this
//     console.log("QR data type:", typeof decodedText); // Add this

//     const bookingDetails = await parseQrData(decodedText);
//     const validation = await validateBooking(bookingDetails);
//     console.log("Parsed booking details:", bookingDetails); // Add this

//     if (!validation.isValid) {
//       setErrorMessage(validation.message);
//       setShowModal(true);
//       setIsScanning(true);
//       return;
//     }

//     if (bookingDetails) {
//       try {
//         // Check if early check-in disclaimer should be shown
//         if (shouldShowDisclaimer(bookingDetails.checkin_date)) {
//           // Call pre-checkin API to get early check-in details
//           const preCheckinResponse = await callPreCheckinAPI(
//             bookingDetails.order_id,
//             bookingDetails.guest_name.split(" ").pop(), // Last name
//             bookingDetails.guest_email
//           );

//           // Check if already checked in
//           if (
//             preCheckinResponse.bookings &&
//             preCheckinResponse.bookings[0] &&
//             preCheckinResponse.bookings[0].status_message ===
//               "Already checked in"
//           ) {
//             // Merge the original booking details with the pre-checkin response
//             const mergedBookingDetails = {
//               ...bookingDetails,
//               ...preCheckinResponse,
//               primaryBooking: {
//                 ...bookingDetails.primaryBooking,
//                 ...(preCheckinResponse.bookings
//                   ? preCheckinResponse.bookings[0]
//                   : {}),
//               },
//               bookings: preCheckinResponse.bookings || bookingDetails.bookings,
//             };

//             // Store the merged booking details
//             localStorage.setItem(
//               "bookingDetails",
//               JSON.stringify(mergedBookingDetails)
//             );
//             sessionStorage.setItem(
//               "bookingDetails",
//               JSON.stringify(mergedBookingDetails)
//             );
//             setScanSuccess(true);

//             setTimeout(() => {
//               navigate("/reservation/options/booking/welcome", {
//                 state: { bookingDetails: mergedBookingDetails },
//               });
//             }, 500);
//             return;
//           }

//           // Store early check-in data for the disclaimer
//           if (
//             preCheckinResponse.success &&
//             preCheckinResponse.bookings &&
//             preCheckinResponse.bookings[0] &&
//             preCheckinResponse.bookings[0].early_checkin
//           ) {
//             setEarlyCheckinData(preCheckinResponse.bookings[0].early_checkin);
//           }

//           setScannedBooking(bookingDetails);
//           setShowDisclaimer(true);
//           return;
//         }

//         // If no disclaimer needed, proceed directly with check-in
//         const preCheckinResponse = await callPreCheckinAPI(
//           bookingDetails.order_id,
//           bookingDetails.guest_name.split(" ").pop(), // Last name
//           bookingDetails.guest_email
//         );

//         if (preCheckinResponse.success) {
//           // Check if already checked in
//           if (
//             preCheckinResponse.bookings &&
//             preCheckinResponse.bookings[0] &&
//             preCheckinResponse.bookings[0].status_message ===
//               "Already checked in"
//           ) {
//             // Merge the original booking details with the pre-checkin response
//             const mergedBookingDetails = {
//               ...bookingDetails,
//               ...preCheckinResponse,
//               primaryBooking: {
//                 ...bookingDetails.primaryBooking,
//                 ...(preCheckinResponse.bookings
//                   ? preCheckinResponse.bookings[0]
//                   : {}),
//               },
//               bookings: preCheckinResponse.bookings || bookingDetails.bookings,
//             };

//             // Store the merged booking details
//             localStorage.setItem(
//               "bookingDetails",
//               JSON.stringify(mergedBookingDetails)
//             );
//             sessionStorage.setItem(
//               "bookingDetails",
//               JSON.stringify(mergedBookingDetails)
//             );
//             setScanSuccess(true);

//             setTimeout(() => {
//               navigate("/reservation/options/booking/welcome", {
//                 state: { bookingDetails: mergedBookingDetails },
//               });
//             }, 500);
//           } else {
//             // Merge the original booking details with the pre-checkin response
//             const mergedBookingDetails = {
//               ...bookingDetails,
//               ...preCheckinResponse,
//               primaryBooking: {
//                 ...bookingDetails.primaryBooking,
//                 ...(preCheckinResponse.bookings
//                   ? preCheckinResponse.bookings[0]
//                   : {}),
//               },
//               bookings: preCheckinResponse.bookings || bookingDetails.bookings,
//             };

//             // Store the merged booking details
//             localStorage.setItem(
//               "bookingDetails",
//               JSON.stringify(mergedBookingDetails)
//             );
//             sessionStorage.setItem(
//               "bookingDetails",
//               JSON.stringify(mergedBookingDetails)
//             );
//             setScanSuccess(true);

//             setTimeout(() => {
//               navigate(
//                 validation.isHardBooking
//                   ? "/reservation/options/booking/welcome"
//                   : "/checkin-details",
//                 { state: { bookingDetails: mergedBookingDetails } }
//               );
//             }, 500);
//           }
//         } else {
//           throw new Error(
//             preCheckinResponse.message || "Pre-checkin verification failed"
//           );
//         }
//       } catch (error) {
//         console.error("Pre-checkin error:", error);
//         setErrorMessage(
//           error.message ||
//             "Failed to verify reservation. Please try again or contact support."
//         );
//         setShowModal(true);
//         setIsScanning(true);
//       }
//     } else {
//       setErrorMessage("Invalid QR code data.");
//       setShowModal(true);
//       setIsScanning(true);
//     }
//   };

//   useEffect(() => {
//     const scanner = new Html5QrcodeScanner("qr-reader", {
//       fps: 10,
//       qrbox: { width: 250, height: 250 },
//       rememberLastUsedCamera: true,
//     });

//     scanner.render(handleScanSuccess, (error) => {
//       if (!error.includes("NotFoundException")) {
//         console.warn("Scan error:", error);
//       }
//     });

//     const styleElement = document.createElement("style");
//     styleElement.innerHTML = `
//       .html5-qrcode-branding {
//         display: none !important;
//       }

//       .html5-qrcode-report-issue-link {
//         display: none !important;
//       }

//       .html5-qrcode-element {
//         margin: 12px auto;
//         padding: 10px 24px;
//         background: #000;
//         color: white;
//         border: none;
//         border-radius: 8px;
//         font-weight: 500;
//         cursor: pointer;
//         transition: background 0.2s;
//         display: block;
//         width: 80%;
//         font-size: 14px;
//       }

//       #html5-qrcode-anchor-scan-type-change {
//         margin: 12px auto;
//         padding: 10px 24px;
//         background: #f8f8f8;
//         color: #333;
//         border: 1px solid #ddd;
//         border-radius: 8px;
//         font-weight: 500;
//         cursor: pointer;
//         display: block;
//         width: 80%;
//         font-size: 14px;
//       }

//       .html5-qrcode-button-container {
//         position: relative;
//         margin: 16px 0;
//       }

//       .html5-qrcode-button-container::before {
//         content: "OR";
//         position: absolute;
//         top: -10px;
//         left: 50%;
//         transform: translateX(-50%);
//         background: white;
//         padding: 0 12px;
//         color: #666;
//         font-size: 13px;
//         font-weight: 500;
//         z-index: 1;
//       }

//       .html5-qrcode-button-container::after {
//         content: "";
//         position: absolute;
//         top: 0;
//         left: 0;
//         right: 0;
//         height: 1px;
//         background: #ddd;
//         z-index: 0;
//       }
//     `;
//     document.head.appendChild(styleElement);

//     scannerRef.current = scanner;

//     return () => {
//       scanner.clear().catch((e) => {
//         console.warn("Error clearing scanner:", e);
//       });
//       document.head.removeChild(styleElement);
//     };
//   }, []);

//   return (
//     <div className="flex flex-col items-center justify-center min-h-1 bg-gray-50 p-8">
//       <div className="w-full max-w-md bg-white rounded-xl shadow-md overflow-hidden p-6">
//         <h2 className="text-3xl font-bold text-center mb-2">
//           Scan Your QR Code
//         </h2>
//         <h2 className="text-sm font-semibold text-gray-600 text-center mb-4">
//           Please scan the QR code received in the confirmation email
//         </h2>
//         <div
//           id="qr-reader"
//           className="w-full h-full rounded-lg border-4 border-dashed border-blue-200 bg-gray-100 overflow-hidden relative"
//         ></div>

//         {scanSuccess && (
//           <div className="absolute inset-0 flex items-center justify-center  rounded-lg">
//             <div className="bg-white p-10 rounded-lg shadow-xl">
//               <div className="flex justify-center mb-2">
//                 <svg
//                   className="w-12 h-12 text-green-500"
//                   fill="none"
//                   stroke="currentColor"
//                   viewBox="0 0 24 24"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M5 13l4 4L19 7"
//                   />
//                 </svg>
//               </div>
//               <p className="text-center font-semibold text-gray-800">
//                 Scanned Successfully!
//               </p>
//             </div>
//           </div>
//         )}
//       </div>

//       {showModal && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
//           <div className="relative bg-white rounded-lg shadow-lg max-w-[500px] w-full p-6 mx-4">
//             <div className="text-center">
//               <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-3" />
//               <h2 className="text-gray-900 text-lg font-semibold mb-2">
//                 Unable to Proceed
//               </h2>
//               <p className="text-red-600 text-md font-medium mb-3 whitespace-pre-line">
//                 {errorMessage}
//               </p>
//               <p className="text-gray-700 text-sm mb-4 mt-2">
//                 The QR code has been sent to your registered email address upon
//                 reservation. For expired or incorrect reservations, please
//                 contact{" "}
//                 <a
//                   href={`mailto:${hotelInfo.email}`}
//                   className="text-blue-600 hover:underline"
//                 >
//                   {hotelInfo.name}
//                 </a>{" "}
//                 support at{" "}
//                 <a
//                   href={`mailto:${hotelInfo.email}`}
//                   className="text-blue-600 hover:underline"
//                 >
//                   {hotelInfo.email}
//                 </a>
//                 .
//               </p>

//               <button
//                 onClick={() => {
//                   setShowModal(false);
//                   setIsScanning(true);
//                 }}
//                 className="px-5 py-2 bg-black text-white rounded-md font-medium"
//               >
//                 Try Again
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {showDisclaimer && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg max-w-lg w-full p-6">
//             <h2 className="text-xl font-bold mb-4">Early Check-In Notice</h2>
//             <p className="mb-4">
//               You are attempting to check in before the standard check-in time
//               of <strong>1:00 PM</strong>. Early check-in will be charged{" "}
//               <strong>₹{earlyCheckinData?.extra_charge}</strong> and is subject
//               to room availability.
//             </p>

//             <p className="mb-6 font-semibold">
//               Would you like to proceed with the early check-in?
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => {
//                   setShowDisclaimer(false);
//                   setIsScanning(true);
//                 }}
//                 disabled={processing}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={proceedWithCheckIn}
//                 disabled={processing}
//                 className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
//               >
//                 {processing ? "Processing..." : "Proceed"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default QRScanner;
